/**
 * SINGLE SOURCE OF TRUTH for "is this order in the Staging Blotter?"
 *
 * ---------------------------------------------------------------------------
 * WHY THIS FILE EXISTS
 * ---------------------------------------------------------------------------
 * Historically an order was deemed to be in the Staging Blotter by string-comparing
 * its venue: `order.venue === "Trading Desk"`. Order-entry code *overwrote* `venue`
 * with that literal to push an order into staging, which destroyed the original
 * inventory venue (the broker/dealer the inventory row came from).
 *
 * That inference is being replaced by an explicit flag column on `order_mst`, where
 * value `0` means the order IS in the Staging Blotter. `venue` is then free to carry
 * the original inventory venue, preserved even while the order sits in staging.
 *
 * The agreed column is `order_version` on `order_mst`, DEFAULT 1, where 0 means the
 * order is in the Staging Blotter. (Note: it is unrelated to `order_mst.version`,
 * the pre-existing JPA optimistic-lock counter.)
 *
 * Every staging decision in the frontend routes through this file so that when the
 * backend column lands, the change is confined here:
 *
 *   - the DTO key                    -> STAGING_FLAG_KEY
 *   - which value means staging      -> STAGING_FLAG_VALUES
 *   - how staging is decided         -> isStagingOrder / isLiveOrder
 *   - how an order is MARKED staged  -> stagingOrderFields
 *
 * ---------------------------------------------------------------------------
 * CURRENT STATE: FLAG-BASED LOGIC IS LIVE
 * ---------------------------------------------------------------------------
 * The flag is the active code path. Each function keeps its previous venue-string
 * implementation directly beneath, commented out, as a one-line revert if the flag
 * turns out to be missing from some payload. Delete those once it is confirmed
 * everywhere.
 *
 * REQUIRES from the backend:
 *   - `orderVersion` on the order payload from BOTH /order and /staging-order, AND
 *     on the websocket orderUpdate event -- live blotter routing reads it from the
 *     socket event independently of the REST fetch, so a payload carrying it in one
 *     place but not the other makes rows jump between blotters on every update.
 *   - the backend also still treats `venue === "Trading Desk"` as staged, via a
 *     compatibility bridge, so the two sides can deploy in either order.
 */

// ---------------------------------------------------------------------------
// The flag
// ---------------------------------------------------------------------------

/**
 * The staging flag's key on the order DTO.
 *
 * The database column is `order_version`; the JSON key the API serialises is
 * `orderVersion`. The backend configures no Jackson property-naming strategy and
 * puts no @JsonProperty on the order DTOs, so Jackson uses the literal Java field
 * name -- hence camelCase here, snake_case in the DDL. Do not "correct" this to
 * `order_version`.
 */
export const STAGING_FLAG_KEY = "orderVersion";

/**
 * What the flag's values mean. Per the backend spec: 0 -> Staging Blotter,
 * and the column defaults to 1, so anything non-zero is live at a market venue.
 */
export const STAGING_FLAG_VALUES = {
  STAGED: 0,
  LIVE: 1,
};

/**
 * The venue string that used to double as the staging marker.
 *
 * It is no longer how staging is decided -- the flag above is -- but it remains the
 * fallback `venue` for the flows that have no inventory dealer to preserve, so that
 * nothing downstream sees a different value for those orders than it did before.
 * The backend also still accepts it as a staging signal via its compatibility bridge.
 *
 * TEMPORARY: pending a decision on whether those flows should send null instead.
 */
export const LEGACY_STAGING_VENUE = "Trading Desk";

// ---------------------------------------------------------------------------
// Predicates -- "is this order staged?"
// ---------------------------------------------------------------------------

/**
 * True when the given order belongs in the Staging Blotter.
 *
 * Accepts anything order-shaped: an API row, a websocket update payload, a grid
 * `rowData`, or an in-flight order-entry draft.
 *
 * @param {object} order
 * @returns {boolean}
 */
export const isStagingOrder = (order) => {
  // Callers previously threw on a null order; returning false is strictly safer and
  // only affects paths that already crashed.
  if (!order) return false;

  // ---- ACTIVE: explicit flag (0 === staging) ------------------------------
  // Number() so a string "0" from JSON is treated the same as a numeric 0.
  // A missing key yields NaN, which is not 0, so an order whose payload predates
  // the backend column reads as live -- matching the column's DEFAULT 1.
  return Number(order[STAGING_FLAG_KEY]) === STAGING_FLAG_VALUES.STAGED;

  // ---- PREVIOUS: legacy venue-string inference ----------------------------
  // Kept for one release as a quick revert if the flag does not arrive on some
  // payload. Delete once the flag is confirmed everywhere.
  // return order.venue === "Trading Desk";
};

/**
 * True when the given order belongs in the regular Order Blotter, i.e. it is live at
 * a market venue. The exact complement of isStagingOrder, so an order can never be
 * claimed by both blotters or dropped by both.
 *
 * @param {object} order
 * @returns {boolean}
 */
export const isLiveOrder = (order) => !isStagingOrder(order);

// ---------------------------------------------------------------------------
// Marking an order as staged (the write side)
// ---------------------------------------------------------------------------

/**
 * The fields that mark a NEW order as belonging to the Staging Blotter. Spread into
 * an order-creation payload.
 *
 * Pass the order's original inventory venue (the inventory row's broker/dealer) when
 * the call site has one -- it is now PRESERVED rather than overwritten, which is the
 * entire point of this change. Where a call site genuinely has no such value, call
 * with no argument and the backend derives it from `bondInventoryId`.
 *
 * @param {string} [inventoryVenue] the originating inventory row's broker/dealer
 * @returns {object} fields to spread into the payload
 */
export const stagingOrderFields = (inventoryVenue) => {
  // The staging flag is always sent explicitly -- that is the real signal, and it no
  // longer depends on whatever `venue` happens to hold.
  //
  // Venue: where the call site knows the originating inventory dealer it is preserved,
  // which is the point of this change. Where it does NOT (portfolio-rebalance bulk
  // submit, and the characteristic/ladder draft seeds -- none of which have a dealer
  // yet), we fall back to the historical "Trading Desk" literal rather than sending
  // null.
  //
  // TEMPORARY, pending confirmation: null is arguably the honest value for those flows
  // -- they genuinely have no dealer until the trader shops the order -- but sending it
  // changes what every downstream consumer of `venue` sees for those orders. Keeping
  // the old literal means nothing downstream shifts while that is being confirmed.
  // To adopt null later, change this fallback to `?? null` and nothing else.
  return {
    venue: inventoryVenue ?? LEGACY_STAGING_VENUE,
    [STAGING_FLAG_KEY]: STAGING_FLAG_VALUES.STAGED,
  };
};

/**
 * The counterpart to stagingOrderFields: the fields that move a draft OUT of the
 * Staging Blotter because the trader has chosen a real market destination.
 *
 * Picking a dealer in the send-to-market modal is precisely the act that makes an
 * order live, so this doubles as the "the trader actually confirmed a dealer" signal
 * the submit gates need. Before this existed, those gates inferred it from
 * `venue === "Trading Desk"`, which stopped working the moment venue began holding a
 * real inventory dealer.
 *
 * @param {string} marketVenue the dealer/venue the trader selected
 * @returns {object} fields to merge into the order draft
 */
export const liveOrderFields = (marketVenue) => ({
  venue: marketVenue,
  [STAGING_FLAG_KEY]: STAGING_FLAG_VALUES.LIVE,
});

// ---------------------------------------------------------------------------
// Venue labels -- NOT the staging flag
// ---------------------------------------------------------------------------

// Note: an earlier `isStagingVenueValue(venueString)` helper lived here for call sites
// that held only a bare venue string. It has been removed: a venue string alone cannot
// determine staging under this model, and every one of those call sites now passes the
// order object to isStagingOrder() instead. Do not reintroduce it.

/**
 * Whether a venue OPTION LABEL from the dealer dropdown is the Trading Desk
 * pseudo-venue.
 *
 * NOT part of the staging flag and does NOT change when the flag lands. "Trading
 * Desk" remains a label that must never be offered as a real market destination.
 * It lives here only so that every "Trading Desk" literal in the frontend has a
 * single home.
 *
 * @param {string} optionLabel
 * @returns {boolean}
 */
/**
 * Shown when the form is raising an order that is bound for the Staging Blotter regardless
 * of the dealer chosen — currently "Add Order" on a ladder-linked characteristic, whose
 * Dealer field is editable so the trader can record the inventory it came from. Without
 * this the enabled Dealer field reads as "pick where to send it", which is not what it
 * does: the staging flag decides routing, not the venue.
 */
export const STAGING_ROUTE_NOTE = "Order will be routed to Staging Blotter.";

export const isTradingDeskVenueLabel = (optionLabel) =>
  /trading\s+desk/i.test(optionLabel);
